/**
 * Plan complexe — morceaux partagés par les modes : la figure (repère
 * d'Argand, axes « Re » et « Im »), les points posés, les arcs d'argument et
 * la liste des étapes de calcul.
 */

import type { ReactNode, RefObject, SVGProps } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { frenchSpaces } from '../../ui/format';
import { makeFrame, type Frame } from '../../ui/frame';
import { FIGURE_BG_CLASS, HaloText, INK, PlotGrid } from '../../ui/svg-parts';
import type { Step, Z } from './complex';

export const SIZE = 320;
export const VP = { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
export const frame = makeFrame(VP, SIZE);

export const Z1_COLOR = 'var(--color-primary-500)';
export const Z2_COLOR = 'var(--color-accent-500)';
export const RES_COLOR = 'var(--color-success-500)';
export const SOFT = 'var(--color-ink-400)';

/** le point est-il dans la fenêtre ? */
export const inView = (z: Z, f: Frame = frame, margin = 0) =>
  z.a >= f.vp.xMin + margin - 1e-9 &&
  z.a <= f.vp.xMax - margin + 1e-9 &&
  z.b >= f.vp.yMin + margin - 1e-9 &&
  z.b <= f.vp.yMax - margin + 1e-9;

/** marge des points calculés : leur nom doit tenir dans la figure */
export const LABEL_MARGIN = 0.25;

/** position écran d'un complexe */
export const px = (z: Z, f: Frame = frame) => ({ x: f.tx(z.a), y: f.ty(z.b) });

/** décalage d'une étiquette vers l'extérieur (loin de O) */
export function outward(z: Z, d = 18): { dx: number; dy: number } {
  const n = Math.hypot(z.a, z.b);
  if (n < 1e-9) return { dx: 13, dy: -13 };
  return { dx: (z.a / n) * d, dy: (-z.b / n) * d };
}

/**
 * Décalage d'une étiquette gardé dans la figure : retourné vers l'intérieur
 * près d'un bord, puis borné à [pad ; size − pad].
 */
export function keepInside(
  x: number,
  y: number,
  o: { dx: number; dy: number },
  size: number = SIZE,
  pad = 12,
): { dx: number; dy: number } {
  let { dx, dy } = o;
  if (x + dx < pad || x + dx > size - pad) dx = -dx;
  if (y + dy < pad || y + dy > size - pad) dy = -dy;
  const cl = (v: number) => Math.min(size - pad, Math.max(pad, v));
  return { dx: cl(x + dx) - x, dy: cl(y + dy) - y };
}

/** Résumé court pour le lecteur d'écran (les formules ne sont pas relues). */
export function Live({ text }: { text: string }) {
  return (
    <p className="sr-only" aria-live="polite">
      {text}
    </p>
  );
}

/**
 * Arc de centre (cx, cy), rayon r (px), de l'angle `from` à l'angle `to`
 * (radians, sens trigonométrique, y vers le haut) : l'écart pris dans
 * ]−π ; π], un demi-tour exact passant par le haut.
 */
export function arcPath(cx: number, cy: number, r: number, from: number, to: number): string {
  let d = to - from;
  while (d <= -Math.PI) d += 2 * Math.PI;
  while (d > Math.PI + 1e-12) d -= 2 * Math.PI;
  if (Math.abs(d) < 1e-9) return '';
  const f = (n: number) => n.toFixed(2);
  const x1 = cx + r * Math.cos(from);
  const y1 = cy - r * Math.sin(from);
  const x2 = cx + r * Math.cos(from + d);
  const y2 = cy - r * Math.sin(from + d);
  return `M ${f(x1)} ${f(y1)} A ${r} ${r} 0 0 ${d > 0 ? 0 : 1} ${f(x2)} ${f(y2)}`;
}

/** milieu angulaire de l'arc ci-dessus */
export function arcMid(from: number, to: number): number {
  let d = to - from;
  while (d <= -Math.PI) d += 2 * Math.PI;
  while (d > Math.PI + 1e-12) d -= 2 * Math.PI;
  return from + d / 2;
}

/** La figure : repère, quadrillage, noms des axes. */
export function Plane({
  svgRef,
  f = frame,
  label,
  step = 1,
  svgProps,
  children,
}: {
  svgRef?: RefObject<SVGSVGElement | null>;
  f?: Frame;
  label: string;
  step?: number;
  svgProps?: SVGProps<SVGSVGElement>;
  children: ReactNode;
}) {
  const oy = f.ty(0);
  const ox = f.tx(0);
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${f.size} ${f.size}`}
        className={cn(
          'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="group"
        aria-label={label}
        {...svgProps}
      >
        <PlotGrid frame={f} step={step} />
        <g aria-hidden fontSize={13} fontStyle="italic" fill="var(--color-ink-500)">
          <HaloText x={f.size - 6} y={oy - 8} textAnchor="end">
            Re
          </HaloText>
          <HaloText x={ox - 6} y={14} textAnchor="end">
            Im
          </HaloText>
        </g>
        {children}
      </svg>
    </div>
  );
}

/** Point posé (non déplaçable), avec son nom. */
export function PointMark({
  z,
  color,
  label,
  f = frame,
  overline = false,
  offset,
  testId,
}: {
  z: Z;
  color: string;
  label: string;
  f?: Frame;
  /** nom surligné : le conjugué z̄ */
  overline?: boolean;
  /** décalage imposé de l'étiquette (px), sinon vers l'extérieur */
  offset?: { dx: number; dy: number };
  testId?: string;
}) {
  if (!inView(z, f, f === frame ? LABEL_MARGIN : 0)) return null;
  const p = px(z, f);
  const o = keepInside(p.x, p.y, offset ?? outward(z), f.size);
  return (
    <g aria-hidden data-testid={testId}>
      <circle cx={p.x} cy={p.y} r={5.5} fill={color} />
      <HaloText
        x={p.x + o.dx}
        y={p.y + o.dy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={14}
        fontWeight={700}
        fill={INK}
        style={overline ? { textDecoration: 'overline' } : undefined}
      >
        {label}
      </HaloText>
    </g>
  );
}

/** Étapes de calcul : la formule, puis la phrase qui l'explique. */
export function StepList({ steps, testId }: { steps: readonly Step[]; testId?: string }) {
  return (
    <ol className="space-y-2" data-testid={testId}>
      {steps.map((s, i) => (
        <li key={i} className="border-ink-100 rounded-2xl border px-3 py-2">
          <MathText className="text-ink-900 block overflow-x-auto py-0.5">{`$${s.tex}$`}</MathText>
          {s.say && <p className="text-ink-600 mt-1 text-sm">{frenchSpaces(s.say)}</p>}
        </li>
      ))}
    </ol>
  );
}

/** Encadré titré (« Module ») avec son contenu et, au besoin, un bouton à droite. */
export function Box({
  title,
  action,
  children,
  testId,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2" data-testid={testId}>
      <div className="flex min-h-11 items-center gap-2">
        <h3 className="text-ink-600 min-w-0 flex-1 text-sm font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Formule seule, défilable si elle est trop longue pour l'écran. */
export function Formula({ tex, className }: { tex: string; className?: string }) {
  return (
    <MathText className={cn('text-ink-900 block overflow-x-auto py-0.5', className)}>
      {`$${tex}$`}
    </MathText>
  );
}

/** a + ib approché : « 0{,}309 + 0{,}951i » */
export function approxZTex(z: Z, digits = 3): string {
  const t = (v: number) => {
    const r = Number(v.toFixed(digits));
    return String(Object.is(r, -0) ? 0 : r).replace('.', '{,}');
  };
  const re = t(z.a);
  const imAbs = t(Math.abs(z.b));
  if (imAbs === '0') return re;
  const im = imAbs === '1' ? 'i' : `${imAbs}i`;
  if (re === '0') return z.b < 0 ? `-${im}` : im;
  return `${re} ${z.b < 0 ? '-' : '+'} ${im}`;
}
